// angular
import { Injectable } from "@angular/core";
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// global-services

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

// interface-components models
import {
  ICAutocompleteGenericModel,
  ICTenorValueDateModel,
  ICMarkupModel,
} from "../interface/controls/models/bfx-interface-controls.models";
import { ICGoodFromUntilModel } from "../interface/controls/models/bfx-good-from-until.model";
import { NOSECS_VALIDATE_TIME_PATTERN } from "../interface/controls/bfx-time-input.component";
// order-entry services
import { OECoordinatorService } from "./oe-coordinator.service";
import { OEModelGetterService } from "./model/oe-model-getter.service";
import { OEModelSetterService } from "./model/oe-model-setter.service";
import { OEToleranceService } from "./basic/oe-tolerance.service";
import { OEUtilityService } from "./basic/oe-utility.service";
import { OETimeMachineService } from "./basic/oe-time-machine.service";
import { OELoggerService } from "./basic/oe-logger.service";
// order-entry models
import { OEInputModel } from "../data/models/oe-input.model";
import {
  OELockingProfileModel,
  OEPrivatizationLockingProfileModel,
  OEInputLockStateItemModel,
} from "../data/models/oe-locking-profile.model";
// order-entry enums
import { OENPanel } from "../data/enums/oe-panel.enum";
import { OENInputControl } from "../data/enums/oe-input-control.enum";
import { OENCommand } from "../data/enums/oe-command.enum";
import { OENLockedState } from "../data/enums/oe-locked-state.enum";
import { OEDLoadingDataModel } from "../data/models/oe-loading-data.model";
import { OEDatasetService } from "./basic/oe-dataset.service";
import { ICLimitPriceModel } from "../interface/controls/models/ic-limit-price.model";
import { ATDLValidatorService } from "./atdl/atdl-validator.service";
import { OEDataModel } from "../data/models/oe-data.model";
import { ICDTransportModel } from "../data-idc/coordination/icd-transport.model";
import { OENEntryType } from "app/order-entry/data/enums/oe-entry-type.enum";
import { ATDLDatasetService } from "./atdl/atdl-dataset.service";
import { ICDNAuxMode } from "../data-idc/enums/icd-aux-mode.enum";
import { OELookupService } from "./basic/oe-lookup.service";
import { OESettingsService } from "./basic/oe-settings.service";
import { OEStrategyModel } from "../data/models/oe-strategy.model";
import { ICD_ID_LIMIT_PRICE } from "../interface/oe-panel-algo-params.const";

@Injectable()
export class OEValidatorService {
  /**********************************************************************/
  /* CONSTRUCTOR */

  constructor(
    private oeLogger: OELoggerService,
    //private loader: OELoaderService,
    private coordinator: OECoordinatorService,
    private getter: OEModelGetterService,
    private setter: OEModelSetterService,
    private tolerance: OEToleranceService,
    private utility: OEUtilityService,
    private timeMachine: OETimeMachineService,
    private atdlValidator: ATDLValidatorService,
    private atdlDataset: ATDLDatasetService,
    private oeLookup: OELookupService,
    private settings: OESettingsService,
    private datasetService: OEDatasetService,
  ) {
    this.oeLogger = oeLogger.for(__dirname);
  }

  /**********************************************************************/
  /* MODEL VALIDATION */

  /**
   * This method only validates the required fields of the OrderEntryData model, it does not display errors on the interface, and it does not create the OrderRequest model
   * @param refId: string - Order instance that is being validated
   * @param errors: Array<OEInputModel> - Array with errors on which the model failed
   * @param modeLoad: boolean - Flag for applying different validation rules when the model being validated has just been loaded (for modify)
   * @return boolean - indicates whether the required fields are in the OrderEntryDataModel instance
   */
  // MARKET ORDERS
  public validateOrderData(
    refId: string,
    errors: Array<OEInputModel> = null,
    modeLoad: boolean = false,
    privatized: boolean = false,
  ): boolean {
    try {
      let model = this.getter.get(refId);
      let rootModel = this.getter.getRootModel(refId);
      let lockingProfile: OELockingProfileModel =
        OEPrivatizationLockingProfileModel.getLockIntersection(
          model.strategy.lockProfile,
          model.privatizationLockProfile,
        );

      if (errors == null) {
        errors = new Array<OEInputModel>();
      }
      // reset previously reported errors
      this.setter.resetValidationErrors(refId);

      // Debug
      //this.config.debug ? this.getter.logOrderData(refId, ' VALIDATE') : '';

      // CLIENT
      if (
        !(model.isLockedForModification || (modeLoad && privatized)) &&
        (model.client == null || !(model.client instanceof CubiqTS.ClientImpl))
      ) {
        errors.push(new OEInputModel(OENPanel.PANEL_CLIENT, OENInputControl.CLIENT));
      } else if (
        !(model.client instanceof CubiqTS.ClientImpl) || // additional client checks
        (!privatized && !model.client.ordersEnabledFg) ||
        (model.entryType === OENEntryType.ALGO && !model.client.autoFillEnabled) ||
        (model.entryType === OENEntryType.BENCHMARK &&
          !this.oeLookup.isBranchBenchmarkEnabled(model.client.branchCd))
      ) {
        errors.push(new OEInputModel(OENPanel.PANEL_CLIENT, OENInputControl.CLIENT));
      }

      // ACCOUNT - MUST BE AFTER CLIENT VALIDATION
      if (
        !(model.isLockedForModification || (modeLoad && privatized)) &&
        (model.account == null || !(model.account instanceof CubiqTS.AccountImpl))
      ) {
        let hasAccountError = true;
        const isBenchmark = model?.orderType?.value === CubiqTS.RequestOrderType.BMARK;
        if (isBenchmark) {
          // We allow blank account for mutli-allocation benchmarks
          const multiAlloc = model.allocations?.length > 1;
          hasAccountError = !multiAlloc;
        }
        if (hasAccountError) {
          errors.push(new OEInputModel(OENPanel.PANEL_CLIENT, OENInputControl.ACCOUNT));
        }
      } else if (
        model.account instanceof CubiqTS.AccountImpl && // account is valid
        errors.findIndex((item) => item.input === OENInputControl.CLIENT) === -1 && // client is valid (no error)
        !(model.isLockedForModification || (modeLoad && privatized))
      ) {
        // client is known
        if (
          model.account.salesGroupId == null &&
          Array.isArray(model.account.associatedClients) &&
          model.account.associatedClients.length > 0 &&
          model.account.associatedClients.findIndex((item) => item === model.client.clientId) > -1
        ) {
          // client/association is valid
        } else if (
          model.account.salesGroupId instanceof CubiqTS.Long &&
          model.client.salesGroupId instanceof CubiqTS.Long &&
          model.account.salesGroupId.equals(model.client.salesGroupId)
        ) {
          // break account can be used by the client
        } else {
          errors.push(new OEInputModel(OENPanel.PANEL_CLIENT, OENInputControl.ACCOUNT));
        }
      }

      // CCY-PAIR
      if (
        model.ccyPair == null ||
        !(typeof model.ccyPair === "string") ||
        model.ccyPairModel == null ||
        !(model.ccyPairModel instanceof CubiqTS.CurrencyPairImpl) ||
        // if not MKT order the ccyPair should be the same with the root
        (model.orderType instanceof ICAutocompleteGenericModel &&
          model.orderType.value != null &&
          model.orderType.value !== CubiqTS.RequestOrderType.MARKET &&
          rootModel instanceof OEDataModel &&
          model.ccyPair != rootModel.ccyPair)
      ) {
        errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.CCY_PAIR));
      }
      // ORDER-TYPE
      if (
        model.orderType == null ||
        !(model.orderType instanceof ICAutocompleteGenericModel) ||
        model.orderType.value == null ||
        model.orderType.value < 0
      ) {
        errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.ORDER_TYPE));
      }

      // TRADER-GROUP (not required)
      if (!(model.traderGroup === null || model.traderGroup instanceof CubiqTS.TradingGroupImpl)) {
        errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.TRADER_GROUP));
      }

      // CLIENT TRADER (null or valid)
      if (
        model.clientTrader != null &&
        !(
          model.clientTrader instanceof CubiqTS.UserImpl ||
          model.clientTrader.value instanceof CubiqTS.UserImpl
        )
      ) {
        errors.push(new OEInputModel(OENPanel.PANEL_CLIENT, OENInputControl.TRADER));
      }

      /*  ALGO -> Do not evaluate: Tenor(value date) and Request Rate */
      if (
        model.orderType instanceof ICAutocompleteGenericModel &&
        model.orderType.value === CubiqTS.RequestOrderType.ALGO
      ) {
        // ALGO - TYPE
        let algoTypeMode = ICDNAuxMode.DISABLED;
        let algoTypeDataset = this.atdlDataset.getAllAlgosForAutoComplete();
        if (
          model.algoType == null ||
          !(model.algoType instanceof ICAutocompleteGenericModel) ||
          !(algoTypeDataset instanceof Array) ||
          algoTypeDataset.map((item) => item.value).indexOf(model.algoType.value) < 0
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.ALGO_TYPE));
        } else {
          algoTypeMode = this.atdlDataset.getLimitPriceMode(model.algoType.value);
        }
        // LIMIT-PRICE
        if (
          algoTypeMode === ICDNAuxMode.REQUIRED ||
          (algoTypeMode === ICDNAuxMode.OPTIONAL && model.limitPrice instanceof ICLimitPriceModel)
        ) {
          // if DISABLED do not check
          if (
            !(model.limitPrice instanceof ICLimitPriceModel) ||
            // case 1: its required but not activated
            (model.limitPrice.mode === ICDNAuxMode.REQUIRED && !model.limitPrice.activated) ||
            // case 2: its activated but value is null/0
            (model.limitPrice.mode !== ICDNAuxMode.DISABLED &&
              model.limitPrice.activated &&
              (!(model.limitPrice.value instanceof CubiqTS.BigNumber) ||
                !model.limitPrice.value.isGreaterThan(0)))
          ) {
            errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.LIMIT_PRICE));
          }
        }
        // AMOUNT
        if (
          model.orderAmount == null ||
          !(model.orderAmount instanceof CubiqTS.BigNumber) ||
          (!modeLoad &&
            (<CubiqTS.BigNumber>model.orderAmount).isEqualTo(0) &&
            model.bulked == false)
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.ORDER_AMOUNT));
        }
        // CLIENT INTENT
        if (
          model.clientIntent == null ||
          !(typeof model.clientIntent === "number") ||
          model.clientIntent > 1 ||
          model.clientIntent < 0
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.CLIENT_INTENT));
        }
        // FIXED-CCY
        if (
          model.fixedCcy == null ||
          model.fixedCcy !== Object(model.fixedCcy) ||
          model.fixedCcy.ccy == null ||
          !(typeof model.fixedCcy.ccy === "string") ||
          model.fixedCcy.ccy.length !== 3
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.FIXED_CCY));
        }

        /// ALGO PARAMETERS
        this.validateAlgoParameters(model, errors);

        /* MARKET Order */
      } else if (
        model.orderType instanceof ICAutocompleteGenericModel &&
        model.orderType.value === CubiqTS.RequestOrderType.MARKET
      ) {
        // CLIENT INTENT
        if (
          model.clientIntent == null ||
          !(typeof model.clientIntent === "number") ||
          model.clientIntent > 1 ||
          model.clientIntent < 0
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.CLIENT_INTENT));
        }
        // FIXED-CCY
        if (
          model.fixedCcy == null ||
          model.fixedCcy !== Object(model.fixedCcy) ||
          model.fixedCcy.ccy == null ||
          !(typeof model.fixedCcy.ccy === "string") ||
          model.fixedCcy.ccy.length !== 3
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.FIXED_CCY));
        } else if (typeof model.ccyPair === "string") {
          if (
            model.fixedCcy.ccy != model.ccyPair.substr(0, 3) &&
            model.fixedCcy.ccy != model.ccyPair.substr(3, 3)
          ) {
            errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.FIXED_CCY));
          }
        }

        // AMOUNT
        if (
          model.orderAmount == null ||
          !(model.orderAmount instanceof CubiqTS.BigNumber) ||
          (!modeLoad &&
            (<CubiqTS.BigNumber>model.orderAmount).isEqualTo(0) &&
            model.bulked == false)
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.ORDER_AMOUNT));
        }
        // TRADER GROUP
        // if not null, must be manual
        if (
          model.traderGroup instanceof CubiqTS.TradingGroupImpl &&
          model.traderGroup.isAutoFilledFg
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.TRADER_GROUP));
        }

        // REQUEST RATE
        if (model.requestPrice instanceof CubiqTS.BigNumber) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.REQUEST_PRICE));
        }

        // GOOD UNTIL
        // must be 'GTC' or empty, as empty defaults to GTC
        let goodUntilIsEmpty = this.timeMachine.checkIfGoodFromUntilModelIsEmpty(model.goodUntil);
        if (
          !goodUntilIsEmpty ||
          (model.goodUntil != null && model.goodUntil.marketValue !== "GTC")
        ) {
          //!goodUntilIsNull and not GTC){
          errors.push(
            new OEInputModel(
              OENPanel.PANEL_FINANCIALS,
              OENInputControl.GOOD_UNTIL,
              "must_be_gtc_for_mkt",
            ),
          );
        }

        // ORDER-TYPE
        if (!this.isValidMarketOrderType(model)) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.ORDER_TYPE));
        }

        // VALUE DATE
        // if ccyPair is not fwd enabled, must be spot
        /* 	if(model.ccyPairModel instanceof CubiqTS.CurrencyPairImpl 
					&& !(model.ccyPairModel.allowFwdFwdTriggerFg || model.ccyPairModel.allowFwdSpotTriggerFg)){
						if(!(model.valueDate instanceof ICTenorValueDateModel) 
						|| (model.valueDate instanceof ICTenorValueDateModel && model.valueDate.tenor !== CubiqTS.Tenor.SPOT))
						{
							logger.out.warn(`WARNING: oeValidator: Value Date seems to be invalid since its not SPOT while the ccyPair is not FWD enabled. This error is IGNORED for now.`)
						}
					} */

        /*  NOT CALL -> Do not evaluate: Algo Type, Limit Price */
      } else if (
        model.orderType instanceof ICAutocompleteGenericModel &&
        model.orderType.value !== CubiqTS.RequestOrderType.CALL
      ) {
        // CLIENT INTENT
        if (
          model.clientIntent == null ||
          !(typeof model.clientIntent === "number") ||
          model.clientIntent > 1 ||
          model.clientIntent < 0
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.CLIENT_INTENT));
        }
        // FIXED-CCY
        if (
          model.fixedCcy == null ||
          model.fixedCcy !== Object(model.fixedCcy) ||
          model.fixedCcy.ccy == null ||
          !(typeof model.fixedCcy.ccy === "string") ||
          model.fixedCcy.ccy.length !== 3
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.FIXED_CCY));
        }
        // AMOUNT
        if (
          model.orderAmount == null ||
          !(model.orderAmount instanceof CubiqTS.BigNumber) ||
          (!modeLoad &&
            (<CubiqTS.BigNumber>model.orderAmount).isEqualTo(0) &&
            model.orderType.value !== CubiqTS.RequestOrderType.BMARK)
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.ORDER_AMOUNT));
        }
        // REQUEST RATE
        if (
          !(model.requestPrice instanceof CubiqTS.BigNumber) ||
          !model.requestPrice.isGreaterThan(0)
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.REQUEST_PRICE));
        }
        if (
          model.orderType.value === CubiqTS.RequestOrderType.TSL ||
          model.orderType.value === CubiqTS.RequestOrderType.TSL_ASK ||
          model.orderType.value === CubiqTS.RequestOrderType.TSL_BID
        ) {
          if (
            !(model.trailingPips instanceof CubiqTS.BigNumber) ||
            !model.trailingPips.isGreaterThan(0)
          ) {
            errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.TRAILING_PIPS));
          }

          if (
            model.trailingTriggerProvided &&
            (model.trailingTrigger == null ||
              (model.trailingTrigger instanceof CubiqTS.BigNumber &&
                model.trailingPips.isLessThanOrEqualTo(0)))
          ) {
            errors.push(
              new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.TRAILING_TRIGGER),
            );
          }
        }
        // TENOR
        if (
          model.valueDate == null ||
          model.valueDate.value == null ||
          !(model.valueDate instanceof ICTenorValueDateModel) ||
          this.vdIsInvalid(model.valueDate)
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.VALUE_DATE));
        }

        /*  CALL -> Do not evaluate client intent, fixed ccy and amount */
      } else if (
        model.orderType instanceof ICAutocompleteGenericModel &&
        model.orderType.value === CubiqTS.RequestOrderType.CALL
      ) {
        // REQUEST RATE
        if (
          !(model.requestPrice instanceof CubiqTS.BigNumber) ||
          !model.requestPrice.isGreaterThan(0)
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.REQUEST_PRICE));
        }
        // TENOR
        if (
          model.valueDate == null ||
          model.valueDate.value == null ||
          !(model.valueDate instanceof ICTenorValueDateModel) ||
          this.vdIsInvalid(model.valueDate)
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.VALUE_DATE));
        }

        /*  NULL -> Do not evaluate client intent, fixed ccy and amount */
      } else if (model.orderType == null) {
        // validate client intent
        if (
          model.clientIntent == null ||
          !(typeof model.clientIntent === "number") ||
          model.clientIntent > 1 ||
          model.clientIntent < 0
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.CLIENT_INTENT));
        }
        // FIXED CCY
        if (
          model.fixedCcy == null ||
          model.fixedCcy !== Object(model.fixedCcy) ||
          model.fixedCcy.ccy == null ||
          !(typeof model.fixedCcy.ccy === "string") ||
          model.fixedCcy.ccy.length !== 3
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.FIXED_CCY));
        }
        // AMOUNT
        if (
          model.orderAmount == null ||
          !(model.orderAmount instanceof CubiqTS.BigNumber) ||
          (!modeLoad &&
            (<CubiqTS.BigNumber>model.orderAmount).isEqualTo(0) &&
            model.bulked == false)
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.ORDER_AMOUNT));
        }
        // REQUEST RATE
        if (
          !(model.requestPrice instanceof CubiqTS.BigNumber) ||
          !model.requestPrice.isGreaterThan(0)
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.REQUEST_PRICE));
        }
        // TENOR
        if (
          model.valueDate == null ||
          model.valueDate.value == null ||
          !(model.valueDate instanceof ICTenorValueDateModel) ||
          this.vdIsInvalid(model.valueDate)
        ) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.VALUE_DATE));
        }
      } else {
        logger.out.warn(
          `WARNING: oeValidator.validateOrderData(): OrderType driven validation no processed. OrderType -> ${model.orderType} `,
        );
      }

      if (
        model.orderType == null ||
        (model.orderType instanceof ICAutocompleteGenericModel &&
          model.orderType.value != CubiqTS.RequestOrderType.CALL)
      ) {
        /* MARKUP - START */
        if (model.markup != null && model.markup instanceof ICMarkupModel) {
          if (
            model.markup.checkbox.value &&
            (model.markup.value == null ||
              model.markup.textValue == "" ||
              model.markup.typeValue == null)
          ) {
            errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.MARKUP));
          }
        }
      }

      if (OENEntryType.NDF == model.entryType && model.fixingSource == null) {
        errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.FIXING_SOURCE));
      } else if (OENEntryType.NDF == model.entryType && model.fixingSource.id == -1) {
        if (!model.clientComment || model.clientComment.value == "") {
          errors.push(new OEInputModel(OENPanel.PANEL_MORE, OENInputControl.CLIENT_COMMENTS));
        }
      }

      /* ACTIVATION - EXPIRY VALIDATION - START */
      // Validate Activation FORMAT
      let goodFromFormatValid = true;
      let goodFromSetButNotNOW = model.goodFrom != null && model.goodFrom.marketValue != "NOW";
      let goodFromIsEmpty = this.timeMachine.checkIfGoodFromUntilModelIsEmpty(model.goodFrom);
      if (goodFromSetButNotNOW) {
        //!goodFromIsNull){
        if (goodFromIsEmpty || !this.validateActivation(model.goodFrom)) {
          errors.push(
            new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.GOOD_FROM, "format"),
          );
          goodFromFormatValid = false;
        }
      }

      // dont validate expiration on modify - invalid good until is acceptable
      // or if the good until interface is locked = nothing the user can do and than he cant change strategies
      let goodUntilLockItem: OEInputLockStateItemModel =
        lockingProfile.getLockProfileForInputElement(OENInputControl.GOOD_UNTIL);
      let goodUntilUnlocked =
        goodUntilLockItem != null && goodUntilLockItem.lockState !== OENLockedState.LOCKED;
      if (!modeLoad && goodUntilUnlocked) {
        // Validate Expiry FORMAT
        let goodUntilFormatValid = true;
        let goodUntilIsEmpty = this.timeMachine.checkIfGoodFromUntilModelIsEmpty(model.goodUntil);
        if (model.goodUntil != null && model.goodUntil.marketValue !== "GTC") {
          //!goodUntilIsNull and not GTC){
          if (goodUntilIsEmpty || !this.validateExpiry(model.goodUntil)) {
            errors.push(
              new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.GOOD_UNTIL, "format"),
            );
            goodUntilFormatValid = false;
          }
        }
        // Validate Activation/Expiry DATE-TIME constraints
        let goodFromUntilConstraintsValid = true;
        if (
          ((goodFromSetButNotNOW && !goodFromIsEmpty) ||
            (model.goodUntil != null && !goodUntilIsEmpty)) &&
          goodFromFormatValid &&
          goodUntilFormatValid
        ) {
          goodFromUntilConstraintsValid = this.validateActivationExpiryConstraints(
            model.goodFrom,
            model.goodUntil,
            errors,
          );
        }
        // Validate Activation/Expiry ON-TIMEOUT constraint
        if (
          model.goodUntil != null &&
          !goodUntilIsEmpty &&
          goodUntilFormatValid &&
          goodFromUntilConstraintsValid
        ) {
          // if these are not valid the error is already there no need to check timeout
          this.validateExpiryTimeout(model.refId, model.goodUntil, errors);
        }
      }
      /* ACTIVATION - EXPIRY VALIDATION - END */

      // market orders can not be suspended
      if (
        model.orderType instanceof ICAutocompleteGenericModel &&
        model.orderType.value === CubiqTS.RequestOrderType.MARKET
      ) {
        if (model.suspend != null && model.suspend.activated === true) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.SUSPEND));
        }
      } else {
        // validate suspension (if specified)
        if (model.suspend != null && model.suspend.activated === true) {
          if (
            model.suspend.suspendFrom == null ||
            model.suspend.suspendFrom.timeValue == null ||
            model.suspend.suspendFrom.timezoneValue == null ||
            model.suspend.suspendTo == null ||
            model.suspend.suspendTo.timeValue == null ||
            model.suspend.suspendTo.timezoneValue == null ||
            typeof model.suspend.suspendFrom.timeValue !== "string" ||
            model.suspend.suspendFrom.timeValue === "" ||
            !NOSECS_VALIDATE_TIME_PATTERN.test(model.suspend.suspendFrom.timeValue) ||
            !(model.suspend.suspendFrom.timezoneValue instanceof ICAutocompleteGenericModel) ||
            model.suspend.suspendFrom.timezoneValue.value === "" ||
            !NOSECS_VALIDATE_TIME_PATTERN.test(model.suspend.suspendTo.timeValue) ||
            typeof model.suspend.suspendTo.timeValue !== "string" ||
            model.suspend.suspendTo.timeValue === "" ||
            !(model.suspend.suspendTo.timezoneValue instanceof ICAutocompleteGenericModel) ||
            model.suspend.suspendTo.timezoneValue.value === "" ||
            (model.suspend.suspendFrom.timeValue === model.suspend.suspendTo.timeValue &&
              model.suspend.suspendFrom.timezoneValue.value ===
                model.suspend.suspendTo.timezoneValue.value)
          ) {
            errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.SUSPEND));
          }
        }
      }

      /* GAMMA ENTRY */
      if (model.strategy && model.strategy.strategyType == CubiqTS.StrategyType.GAMMA) {
        if (!this.validateDelta(model)) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.DELTA));
        }

        if (!this.validateRungsAbove(model)) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.RUNGS_ABOVE));
        }

        if (!this.validateRungsBelow(model)) {
          errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.RUNGS_BELOW));
        }
      }

      // Custom fields
      const customFieldsValid = this.validateCustomFields(model);
      if (!customFieldsValid) {
        errors.push(new OEInputModel(OENPanel.PANEL_MORE, OENInputControl.CUSTOM_FIELDS));
      }

      // multi-asset allocations
      const allocationsValid = this.validateAllocations(model);
      if (!allocationsValid) {
        errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.ALLOCATIONS));
      }

      let outcome = errors.length === 0;
      if (!outcome) {
        this.setter.reportValidationErrors(refId);
      }
      return outcome;
    } catch (ex) {
      logger.out.error(`EXCEPTION: oeValidator.validateOrderData(): %O`, ex);
      //this.coordinator.cmdChangeDisplayState(refId, OENCommand.STATE_ORDER_INT_ERROR, 'Internal error: exception = ' + ex);
      return false;
    }
  }

  private vdIsInvalid(vd: ICTenorValueDateModel): boolean {
    if (vd && vd.tenor == undefined) {
      if (vd.valueDate) {
        vd.valueDate.setHours(0, 0, 0, 0);
        let now: Date = new Date();
        now.setHours(0, 0, 0, 0);
        if (vd.valueDate.valueOf() < now.valueOf()) {
          return true;
        }
      }
    }
    return false;
  }

  /**********************************************************************/
  /* ALGO PARAMETERS - VALIDATION */

  public validateAlgoParameters(model: OEDataModel, errors: Array<OEInputModel>): boolean {
    if (
      model instanceof OEDataModel &&
      errors instanceof Array &&
      model.algoParameters instanceof Array
    ) {
      if (model.algoParameters.length > 0) {
        let algoParamErrors: Array<ICDTransportModel> = new Array<ICDTransportModel>();
        const algoId = model.algoType.value;
        const parameters = model.algoParameters;

        let outcome: boolean = true;
        if (this.atdlDataset.has(algoId) && parameters instanceof Array && parameters.length > 0) {
          let atdlModel = this.atdlDataset.get(algoId);
          outcome = this.atdlValidator.validateParameters(atdlModel, parameters, algoParamErrors);
        }

        if (!outcome) {
          /// if there are errors send notification
          this.outputExtendedLogging(
            `UI-MAIN: OrderEntry@${Date.now()}: ALGO PARAM [${model.refId}] validation errors:`,
            algoParamErrors,
          );
          if (algoParamErrors instanceof Array && algoParamErrors.length > 0) {
            for (let algoParamError of algoParamErrors) {
              if (algoParamError instanceof ICDTransportModel) {
                if (
                  typeof algoParamError.icdId === "string" &&
                  algoParamError.icdId.toLowerCase() === ICD_ID_LIMIT_PRICE
                ) {
                  // Limit price - split brain
                  errors.push(
                    new OEInputModel(
                      OENPanel.PANEL_FINANCIALS,
                      OENInputControl.LIMIT_PRICE,
                      algoParamError,
                    ),
                  );
                } else {
                  errors.push(
                    new OEInputModel(OENPanel.PANEL_ALGO, OENInputControl._ALGO_, algoParamError),
                  );
                }
              } else {
                logger.out.error(
                  `ERROR: oeValidator.validateOrderData(): algo error not of type ICDTransportModel`,
                );
              }
            }
          } else {
            logger.out.error(
              `ERROR: oeValidator.validateOrderData(): Algo parameter validation ${
                outcome ? "VALID" : "INVALID"
              }, but no errors reported`,
            );
          }
        }
        return outcome;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }

  /**********************************************************************/
  /* TOLERANCE - VALIDATION */

  public validateTolerance(refId: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        if (this.getter.has(refId)) {
          let model = this.getter.getRootModel(refId);
          let algoTypeMode =
            model.algoType != null
              ? this.atdlDataset.getLimitPriceMode(model.algoType.value)
              : ICDNAuxMode.DISABLED;
          if (
            !this.getter.isBenchmarkRequest(refId) &&
            !this.getter.isOrderTypeMKT(refId) &&
            (!(this.getter.isEntryTypeAlgo(refId) || this.getter.isEntryTypeNdfAlgo(refId)) ||
              (model.algoType != null &&
                algoTypeMode !== ICDNAuxMode.DISABLED &&
                model.limitPrice != null &&
                model.limitPrice.activated))
          ) {
            this.tolerance.checkEntryToleranceErrors(model).then((toleranceErrors) => {
              if (toleranceErrors.length === 0) {
                this.setter.setToleranceAccept(refId, true);
                resolve(this.validateAmountTolerance(refId));
              } else {
                // tolerance errors
                let errorInfo = this.tolerance.getEntryToleranceErrorsInfo(toleranceErrors);

                if (this.getter.isGammaStrategy(refId)) {
                  if (errorInfo != null && errorInfo.isWrongSideOfMarket) {
                    this.coordinator.cmdChangeDisplayState(
                      refId,
                      OENCommand.STATE_ORDER_ENTRY_TOLERANCE_ERROR,
                      this.utility.handleErrors(toleranceErrors),
                    );
                  } else {
                    this.coordinator.cmdChangeDisplayState(
                      refId,
                      OENCommand.STATE_ORDER_ENTRY_TOLERANCE_WARN,
                      this.utility.handleErrors(toleranceErrors),
                    );
                  }
                } else if (errorInfo != null && errorInfo.isWrongSideOfMarket) {
                  this.coordinator.cmdChangeDisplayState(
                    refId,
                    OENCommand.STATE_ORDER_ENTRY_TOLERANCE_ERROR,
                    this.utility.handleErrors(toleranceErrors.slice(0, 1)),
                  );
                } else {
                  this.coordinator.cmdChangeDisplayState(
                    refId,
                    OENCommand.STATE_ORDER_ENTRY_TOLERANCE_WARN,
                    this.utility.handleErrors(toleranceErrors.slice(0, 1)),
                  );
                }
                resolve(false);
              }
            });
          } else {
            this.setter.setToleranceAccept(refId, true);
            resolve(this.validateAmountTolerance(refId));
          }
        } else {
          resolve(false);
        }
      } catch (ex) {
        logger.out.error(`EXCEPTION: oeValidator.validateTolerance(): %O`, ex);
        this.coordinator.cmdChangeDisplayState(
          refId,
          OENCommand.STATE_ORDER_INT_ERROR,
          "Internal error: exception = " + ex,
        );
        resolve(false);
      }
    });
  }

  /**********************************************************************/
  /* TOLERANCE AMOUNT - VALIDATION */

  public validateAmountTolerance(refId: string): boolean {
    try {
      if (this.getter.has(refId)) {
        let model = this.getter.getRootModel(refId);

        let toleranceErrors = this.tolerance.checkEntryAmountToleranceErrors(model);
        let errorInfo = this.tolerance.getEntryToleranceErrorsInfo(toleranceErrors);
        if (errorInfo != null && errorInfo.isOutsideEntryAmtTolerance) {
          this.coordinator.cmdChangeDisplayState(
            refId,
            OENCommand.STATE_ORDER_ENTRY_TOLERANCE_WARN,
            this.utility.handleErrors(toleranceErrors),
          );
          return false;
        } else {
          this.setter.setToleranceAmountAccept(refId, true);
          return true;
        }
      } else {
        return false;
      }
    } catch (ex) {
      logger.out.error(`EXCEPTION: oeValidator.validateAmountTolerance(): %O`, ex);
      this.coordinator.cmdChangeDisplayState(
        refId,
        OENCommand.STATE_ORDER_INT_ERROR,
        "Internal error: exception = " + ex,
      );
      return false;
    }
  }

  /**********************************************************************/
  /* TIME CONSTRAINT - Activation Expiry - VALIDATION */

  public validateGoodUntilForAddingTimeout(
    refId: string,
    errors: Array<OEInputModel> = null,
  ): boolean {
    try {
      let model = this.getter.get(refId);
      if (errors == null) {
        errors = new Array<OEInputModel>();
      }
      let numberOfErrorsAlready: number = errors.length;
      let goodUntilIsEmpty = this.timeMachine.checkIfGoodFromUntilModelIsEmpty(model.goodUntil);

      // VALIDATION CONSTRAINT: good until is not set - must be set to add timeout strategy
      if (model.goodUntil == null || goodUntilIsEmpty) {
        errors.push(
          new OEInputModel(
            OENPanel.PANEL_FINANCIALS,
            OENInputControl.GOOD_UNTIL,
            "timeout_parent_required",
          ),
        );
        return false;
      }

      // VALIDATION CONSTRAINT: good until has invalid format
      if (!this.validateExpiry(model.goodUntil)) {
        errors.push(
          new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.GOOD_UNTIL, "format"),
        );
        return false;
      }

      // VALIDATION CONSTRAINT: good until is in the future
      // Handled by regular validation => this.validateOrderData()

      // VALIDATION CONSTRAINT: good until is after good from
      // Handled by regular validation => this.validateOrderData()

      return errors.length === numberOfErrorsAlready;
    } catch (ex) {
      logger.out.error(`EXCEPTION: oeValidator.validateGoodUntilForAddingTimeout(): %O`, ex);
      this.coordinator.cmdChangeDisplayState(
        refId,
        OENCommand.STATE_ORDER_INT_ERROR,
        "Internal error: exception = " + ex,
      );
      return false;
    }
  }

  public validateActivation(model: ICGoodFromUntilModel): boolean {
    return this.validateActivationOrExpiryFormat(model, true);
  }

  public validateExpiry(model: ICGoodFromUntilModel): boolean {
    return this.validateActivationOrExpiryFormat(model, false);
  }

  private validateActivationOrExpiryFormat(
    model: ICGoodFromUntilModel,
    isActivation: boolean,
  ): boolean {
    if (
      model != null &&
      model instanceof ICGoodFromUntilModel &&
      model.dateValue != null &&
      model.dateValue !== "" &&
      model.timeValue != null &&
      model.timeValue !== "" &&
      model.timezoneValue != null &&
      model.timezoneValue !== "" &&
      model.marketValue != null &&
      model.marketValue !== ""
    ) {
      let momentValue = null;
      if (model.marketValue === "CUSTOM") {
        // validate custom
        let timeZoneNamesDataset: Array<string> = this.timeMachine.getTimeZoneNames();
        let timezoneValid: boolean =
          timeZoneNamesDataset != null
            ? timeZoneNamesDataset.filter((item) => {
                return item === model.timezoneValue;
              }).length > 0
            : false;
        if (!timezoneValid) return false;

        momentValue = this.timeMachine.createMomentForModel(model);
      } else {
        // validate market time
        if (model?.dayOrder === true && model?.marketValue === "DAY") return true;
        if (model?.marketValue === "GTC" && !isActivation) return true;
        if (model?.marketValue === "NOW" && isActivation) return true;
        let mtc: CubiqTS.MarketTimeConstantImpl = this.timeMachine.getMarketTimeConstantImpl(
          model.marketValue,
        );
        if (mtc != null) {
          momentValue = this.timeMachine.createMomentForModel(model);
        }
      }
      if (momentValue?.isValid()) {
        return momentValue.isAfter(CubiqTS.moment());
      }
    }
    return false;
  }

  /**
   * The assumption here is that the arguments have been validated using the validateActivationExpiryIndividual() previously.
   * @param modelFrom
   * @param modelUntil
   */
  public validateActivationExpiryConstraints(
    modelFrom: ICGoodFromUntilModel,
    modelUntil: ICGoodFromUntilModel,
    errors: Array<OEInputModel> = null,
  ): boolean {
    let result = true;
    if (errors == null) {
      errors = new Array<OEInputModel>();
    }
    let fromIsEmpty = this.timeMachine.checkIfGoodFromUntilModelIsEmpty(modelFrom);
    let untilIsEmpty = this.timeMachine.checkIfGoodFromUntilModelIsEmpty(modelUntil);

    // Create moments
    let nowMoment = CubiqTS.moment();
    let fromMoment = this.timeMachine.createMoment4Model(modelFrom);
    let untilMoment = this.timeMachine.createMoment4Model(modelUntil);
    // check that the created from moment is valid
    if (
      !fromIsEmpty &&
      modelFrom != null &&
      (fromMoment == null || !(fromMoment instanceof CubiqTS.moment))
    ) {
      errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.GOOD_FROM, "format"));
      result = false;
    }
    //
    if (
      !untilIsEmpty &&
      modelUntil != null &&
      (untilMoment == null || !(untilMoment instanceof CubiqTS.moment))
    ) {
      errors.push(
        new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.GOOD_UNTIL, "format"),
      );
      result = false;
    }
    // escape if errors
    if (!result || (fromIsEmpty && untilIsEmpty)) {
      return result;
    }

    // validate if goodUntil is after goodFrom
    if (
      fromMoment != null &&
      untilMoment != null &&
      (untilMoment.isBefore(fromMoment) || fromMoment.isSame(untilMoment))
    ) {
      logger.out.debug(
        `DEBUG: oeValidator.validateActivationExpiryConstraints(): UNTIL after FROM: UNTIL= ${untilMoment.format()} is before FROM= ${fromMoment.format()} => INVALID!`,
      );
      errors.push(
        new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.GOOD_FROM, "constraint_from"),
      );
      errors.push(
        new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.GOOD_UNTIL, "constraint_until"),
      );
      return false;
    }
    // validate if goodFrom is in the future
    if (
      fromMoment != null &&
      modelFrom.marketValue != "NOW" &&
      (fromMoment.isBefore(nowMoment) || fromMoment.isSame(nowMoment))
    ) {
      logger.out.debug(
        `DEBUG: oeValidator.validateActivationExpiryConstraints(): FROM after NOW: FROM=  ${fromMoment.format()} is before NOW= ${nowMoment.format()} => INVALID!`,
      );
      errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.GOOD_FROM, "future"));
      result = false;
    }
    // validate if goodUntil is in the future
    if (untilMoment != null && (untilMoment.isBefore(nowMoment) || untilMoment.isSame(nowMoment))) {
      logger.out.debug(
        `DEBUG: oeValidator.validateActivationExpiryConstraints(): UNTIL after NOW: UNTIL= ${untilMoment.format()} is before NOW= ${nowMoment.format()} => INVALID!`,
      );
      errors.push(
        new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.GOOD_UNTIL, "future"),
      );
      result = false;
    }
    return result;
  }

  public validateExpiryTimeout(
    refId: string,
    modelUntil: ICGoodFromUntilModel,
    errors: Array<OEInputModel> = null,
  ): boolean {
    if (errors == null) {
      errors = new Array<OEInputModel>();
    }
    try {
      // check if the relationship to the parent is on_timeout or if done
      let relationshipToParent = this.getter.getRelationshipToParent(refId);
      if (
        relationshipToParent != null &&
        (relationshipToParent === CubiqTS.OrderRelationshipType.ON_TIMEOUT ||
          relationshipToParent === CubiqTS.OrderRelationshipType.IFDONE)
      ) {
        // check that this.goodUntil is after parent.goodUntil
        let parentGoodUntil = this.getter.getParentModel(refId).goodUntil;
        if (this.validateExpiry(parentGoodUntil)) {
          // check parent format
          if (this.validateExpiry(modelUntil)) {
            // check this format

            let parentUntilMoment = this.timeMachine.createMoment4Model(parentGoodUntil);
            let untilMoment = this.timeMachine.createMoment4Model(modelUntil);
            if (parentUntilMoment == null || !(parentUntilMoment instanceof CubiqTS.moment)) {
              logger.out.error(
                `ERROR: oeValidator.validateExpiryTimeout(): Parent goodUntil moment is invalid for ${CubiqTS.OrderRelationshipType[relationshipToParent]} relationship for RefId: ${refId}`,
              );
              //TODO: add a switch to strategy or something
            } else {
              // for IF-DONE only before parent can be same, for ON-TIMEOUT before and same not allowed
              if (
                untilMoment.isBefore(parentUntilMoment) ||
                (relationshipToParent === CubiqTS.OrderRelationshipType.ON_TIMEOUT &&
                  untilMoment.isSame(parentUntilMoment))
              ) {
                let errorMsg =
                  relationshipToParent === CubiqTS.OrderRelationshipType.ON_TIMEOUT
                    ? "timeout_before_parent"
                    : "if-done_before_parent";
                errors.push(
                  new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.GOOD_UNTIL, errorMsg),
                );
                return false;
              }
            }
          } else {
            errors.push(
              new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.GOOD_UNTIL, "format"),
            );
            return false;
          }
        } else {
          logger.out.error(
            `ERROR: oeValidator.validateExpiryTimeout(): Parent goodUntil model is invalid for  ${CubiqTS.OrderRelationshipType[relationshipToParent]}  relationship for RefId: ${refId}`,
          );
          //TODO: do something to handle the error
        }
      }
      // if this has on-timeout children
      if (this.getter.hasRelationshipOfType(refId, CubiqTS.OrderRelationshipType.ON_TIMEOUT)) {
        if (!this.validateExpiry(modelUntil)) {
          errors.push(
            new OEInputModel(
              OENPanel.PANEL_FINANCIALS,
              OENInputControl.GOOD_UNTIL,
              "timeout_parent_required",
            ),
          );
          return false;
        }
      }

      return true;
    } catch (ex) {
      logger.out.error(`EXCEPTION: oeValidator.validateExpiryTimeout(): %O`, ex);
      errors.push(new OEInputModel(OENPanel.PANEL_FINANCIALS, OENInputControl.GOOD_UNTIL));
      return false;
    }
  }

  public validateMarkup(refId: string): Promise<boolean> {
    return new Promise((res, rej) => {
      if (this.getter.has(refId)) {
        try {
          let model = this.getter.get(refId);

          if (model.negativeMarkupAccept) {
            res(true);
          }

          if (model.markup && model.markup.value instanceof CubiqTS.BigNumber) {
            if (model.markup.value.isLessThan(0)) {
              this.coordinator.cmdChangeDisplayState(
                refId,
                OENCommand.STATE_ORDER_ENTRY_NEGATIVE_MARKUP,
                {
                  message:
                    model?.markup?.textValue && model?.markup?.typeValue?.label
                      ? ` value of: ${model.markup.textValue} ${model.markup.typeValue.label}`
                      : ``,
                },
              );
              return res(false);
            }
          }

          return res(true);
        } catch (ex) {
          logger.out.error(`EXCEPTION: oeValidator.validateMarkup(): ${refId} %O`, ex);
          res(false);
        }

        res(true);
      }
    });
  }

  /**
   * Check that BM orders have non-zero amount
   * @param refId
   * @returns
   */
  public validateOrderAmount(refId: string): boolean {
    if (this.getter.has(refId)) {
      if (this.getter.isEntryTypeBenchmark(refId)) {
        const model = this.getter.get(refId);
        if (!model.allocations || !model.farAllocations) {
          logger.out.warn("Cannot validate amount - allocations are not setup as exepcted");
          return true;
        }
        const allocationCount = model.allocations.length + model.farAllocations.length;
        if (allocationCount <= 1) {
          if (!model.orderAmount || model.orderAmount.isZero()) {
            // We have a zero benchmark amount so lets get a confirmation from the user
            this.coordinator.cmdChangeDisplayState(refId, OENCommand.BM_ZERO_AMOUNT_WARN, {
              message: ``,
              isAllocation: false,
            });
            return false;
          }
        } else {
          for (const allocation of model.allocations.concat(model.farAllocations)) {
            if (!this.validateAmountForSingleAllocation(refId, allocation)) {
              return false;
            }
          }
        }
      }
    }
    return true;
  }

  /**
   *
   * @param refId
   * @param allocation
   * @returns
   */
  private validateAmountForSingleAllocation(
    refId: string,
    allocation: CubiqTS.AllocationRequestData,
  ): boolean {
    try {
      // Allocation amounts are strings so need toconvert to BigNumber
      if (!allocation.allocationQty || new CubiqTS.BigNumber(allocation.allocationQty).isZero()) {
        // We have a zero benchmark amount so lets get a confirmation from the user
        this.coordinator.cmdChangeDisplayState(refId, OENCommand.BM_ZERO_AMOUNT_WARN, {
          message: ``,
          isAllocation: true,
        });
        return false;
      }
    } catch (e) {
      logger.out.warn("Exception tryin to parse allocation amount");
      // return true here as need to let server handle any error
      return true;
    }
    return true;
  }

  public isGoodFromInThePast(goodFrom: ICGoodFromUntilModel): boolean {
    if (goodFrom != null) {
      let goodFromMoment = this.timeMachine.createMoment4Model(goodFrom);
      let nowMoment = CubiqTS.moment();
      if (
        goodFromMoment != null &&
        goodFromMoment.isValid() &&
        nowMoment.isValid() &&
        goodFromMoment.isBefore(nowMoment)
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  /**********************************************************************/
  /* VALIDATION NOTIFIERS */

  public activateAndNotifyOnValidationErrors(refId: string, errors: Array<OEInputModel>): void {
    if (this.getter.has(refId)) {
      try {
        let model = this.getter.get(refId);
        this.outputExtendedLogging(
          `UI-MAIN: OrderEntry@${Date.now()}: MODEL SWITCH TO [${refId}] | model dump:`,
          model,
        );

        let data: OEDLoadingDataModel = this.utility.createInterfaceLoadDataFromModel(model);
        let lockingProfile: OELockingProfileModel =
          OEPrivatizationLockingProfileModel.getLockIntersection(
            model.strategy.lockProfile,
            model.privatizationLockProfile,
          );

        // activate screen listeners
        this.coordinator.cmdActivateStrategyLegRefListener(refId);
        setTimeout(() => {
          // load data and locking profile
          this.coordinator.cmdLoadInterfaceControlData(refId, data);
          this.coordinator.cmdLoadInterfaceControlLockingProfile(refId, lockingProfile);
        }, 100);

        setTimeout(() => {
          // display errors
          this.notifyOnValidationErrors(refId, errors);
        }, 200);
      } catch (ex) {
        logger.out.error(`EXCEPTION: oeValidator.activateAndNotifyOnValidationErrors(): %O`, ex);
      }
    }
  }

  public notifyOnValidationErrors(refId: string, errors: Array<OEInputModel>): void {
    if (this.getter.has(refId)) {
      try {
        this.coordinator.cmdInvalidInput(refId, errors);
        setTimeout(() => {
          this.coordinator.cmdRefreshStrategyLayout(refId);
        }, 100);
      } catch (ex) {
        logger.out.error(`EXCEPTION: oeValidator.notifyOnValidationErrors(): %O`, ex);
      }
    }
  }

  protected isValidMarketOrderType(model: OEDataModel): boolean {
    // check system setting
    if (model instanceof OEDataModel && this.settings.areMarketOrdersEnabled()) {
      // check if client branch is mkt allowed
      if (
        (model.client instanceof CubiqTS.ClientImpl &&
          this.oeLookup.areMarketOrdersAllowedForClient(model.client)) ||
        model.isModificationPrivatized
      ) {
        // added due to OMS-36177
        // check if ccyPair is mkt enabled
        if (
          model.ccyPairModel instanceof CubiqTS.CurrencyPairImpl &&
          model.ccyPairModel.allowMarketOrdersFg
        ) {
          // check strategy constraints
          if (
            model.strategy == null ||
            (model.strategy instanceof OEStrategyModel &&
              (model.strategy.children == null ||
                (Array.isArray(model.strategy.children) && model.strategy.children.length == 0)) &&
              (model.strategy.siblings == null ||
                (Array.isArray(model.strategy.siblings) && model.strategy.siblings.length == 0)))
          ) {
            return true;
          }
        }
      }
    }
    return false;
  }

  public validateDelta(model: OEDataModel): boolean {
    let delta: string = model.delta;
    let result: boolean = true;

    if (delta != null) {
      try {
        let deltaValue: number = Number(delta);

        if (Number.isNaN(deltaValue)) {
          result = false;
        }

        if (result === true && model.ccyPairModel instanceof CubiqTS.CurrencyPairImpl) {
          let minimumDelta: CubiqTS.Long = model.ccyPairModel.minimumGammaDeltaPips;
          if (
            minimumDelta != null &&
            minimumDelta.toNumber != null &&
            minimumDelta.toNumber() > deltaValue
          ) {
            result = false;
          }
        }
      } catch (e) {
        this.oeLogger.outputDevLogToConsole(`DELTA: Invalid field value ${model.delta}`);
        result = false;
      }
    } else {
      result = false;
    }

    return result;
  }

  public validateRungsAbove(model: any): boolean {
    let rungsAbove: number = model.rungsAbove;
    let result: boolean = true;

    if (rungsAbove != null && !Number.isNaN(model.rungsAbove)) {
      if (rungsAbove < 0) {
        result = false;
      } else if (
        model.rungsBelow != null &&
        !Number.isNaN(model.rungsBelow) &&
        (rungsAbove + model.rungsBelow <= 0 ||
          rungsAbove + model.rungsBelow > this.settings.maximumGammaRungs)
      ) {
        result = false;
      }
    } else {
      result = false;
    }

    return result;
  }

  public validateRungsBelow(model: any): boolean {
    let rungsBelow: number = model.rungsBelow;
    let result: boolean = true;

    if (rungsBelow != null && !Number.isNaN(model.rungsBelow)) {
      if (rungsBelow < 0) {
        result = false;
      } else if (
        model.rungsAbove != null &&
        !Number.isNaN(model.rungsAbove) &&
        (rungsBelow + model.rungsAbove <= 0 ||
          rungsBelow + model.rungsAbove > this.settings.maximumGammaRungs)
      ) {
        result = false;
      }
    } else {
      result = false;
    }

    return result;
  }

  /**********************************************************************/
  /* DEBUG - HELPER  */

  private outputExtendedLogging(value: string, dump: any = null): void {
    this.oeLogger.outputExtendedLogging(value, dump);
  }

  /**
   * Check custom field constriants for missing custom values, and any broken defn constraints
   * @param model
   * @returns
   */
  public validateCustomFields(model: OEDataModel): boolean {
    const fields = this.datasetService.customFieldDefns.editable;

    // Build field map of editable fields
    const fieldMap: Map<string, CubiqTS.CustomField> = new Map();
    fields.forEach((field) => fieldMap.set(field.fieldId, field));

    let isValid = true;
    if (model.customValues) {
      // Now check defined values
      for (let value of model.customValues) {
        if (!value) {
          logger.out.warn(
            `${this.constructor.name}.validateCustomFields() Unexpected null member in value array`,
          );
          continue;
        }

        let field = fieldMap.get(value.fieldId);
        if (field) {
          // If not found, field is non-editable
          fieldMap.delete(value.fieldId);
          const strValue = value.value;
          if (!field.isNullableFg && !strValue) {
            isValid = false;
          } else if (strValue) {
            //
            if (field.freeFormatTxtFg && field.maxLen > 0 && strValue.length > field.maxLen) {
              isValid = false;
            } else if (
              !field.freeFormatTxtFg &&
              field.domainValues &&
              field.domainValues.indexOf(strValue) == -1
            ) {
              isValid = false;
            }
          }
        }
      }
    }
    // Now, only fields in the field map have no fields - ensure none are not nullable
    for (let [id, field] of fieldMap) {
      if (!field.isNullableFg) {
        isValid = false;
      }
    }

    // Would be quicker to return directly from each test above but prefer to have all checks run
    return isValid;
  }

  public validateAllocations(model: OEDataModel) {
    if (model.bulking && model.bulked == false) {
      return false;
    }

    return true;
  }
}
